import { useEffect, useMemo, useState } from 'react';
import { removalTargets } from '@/lib/pdfContentRemoval';
import { saveViaWorker } from '@/lib/saveWorkerClient';
import { pdfjsLib } from '@/lib/pdfWorker';

// Keep the source document/form store stable. Only the page background and
// native text layer use this temporary, content-removed rendering document.
export default function useContentRemovalPreview(pdfDoc, edits, selectedIds) {
  const key = JSON.stringify(removalTargets(edits, { preview: true, selected: [...selectedIds] }));
  const targets = useMemo(() => JSON.parse(key), [key]);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!pdfDoc || !targets.length) return;
    let cancelled = false, preview;
    (async () => {
      try {
        const source = await pdfDoc.getData();
        const bytes = await saveViaWorker({ arrayBuffer: source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength),
          options: { contentRemoval: { targets } } });
        if (cancelled) return;
        preview = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) { await preview.destroy(); return; }
        setResult({ source: pdfDoc, key, document: preview });
      } catch (error) {
        if (!cancelled) setResult({ source: pdfDoc, key, error: error.message });
      }
    })();
    return () => { cancelled = true; if (preview) preview.destroy(); };
  }, [pdfDoc, targets, key]);
  const current = result?.source === pdfDoc && result.key === key ? result : null;
  return { document: targets.length && current?.document || pdfDoc,
    pending: Boolean(pdfDoc && targets.length && !current), error: targets.length ? current?.error : null };
}
